import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import { settingsAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Settings, Save } from 'lucide-react';

const AdminSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      const data = await settingsAPI.get();
      setSettings(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleNestedChange = (parent, field, value) => {
    setSettings(prev => ({
      ...prev,
      [parent]: { ...(prev[parent] || {}), [field]: value }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { id, updatedAt, ...updateData } = settings;
      await settingsAPI.update(updateData);
      alert('Configurações salvas com sucesso!');
    } catch (error) {
      alert('Erro: ' + (error.response?.data?.detail || error.message));
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#055509]" /></div>
      </AdminLayout>
    );
  }

  if (!settings) {
    return (
      <AdminLayout>
        <div className="text-center py-12 text-gray-500">
          <Settings size={48} className="mx-auto mb-4 opacity-50" />
          <p>Configurações não encontradas</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 data-testid="settings-page-title" className="text-2xl font-bold text-gray-800">Configurações</h1>
            <p className="text-gray-600">Configurações gerais do site</p>
          </div>
          <Button data-testid="save-settings-btn" onClick={handleSave} className="bg-[#055509] hover:bg-[#0a7a0f] text-white" disabled={saving}>
            <Save size={16} className="mr-2" />
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>

        <div className="space-y-6">
          {/* General */}
          <Card>
            <CardHeader><CardTitle>Informações Gerais</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Site</label>
                  <Input data-testid="settings-site-name" value={settings.siteName || ''} onChange={e => handleChange('siteName', e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Slogan</label>
                  <Input value={settings.siteSlogan || ''} onChange={e => handleChange('siteSlogan', e.target.value)} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Meta Descrição (SEO)</label>
                <textarea value={settings.metaDescription || ''} onChange={e => handleChange('metaDescription', e.target.value)} className="w-full border rounded-md px-3 py-2 text-sm min-h-[80px]" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL do Logo</label>
                <Input value={settings.logoUrl || ''} onChange={e => handleChange('logoUrl', e.target.value)} />
              </div>
            </CardContent>
          </Card>

          {/* Contact */}
          <Card>
            <CardHeader><CardTitle>Contato</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp</label>
                  <Input data-testid="settings-whatsapp" value={settings.whatsapp || ''} onChange={e => handleChange('whatsapp', e.target.value)} placeholder="5562991953619" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                  <Input value={settings.phone || ''} onChange={e => handleChange('phone', e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <Input value={settings.email || ''} onChange={e => handleChange('email', e.target.value)} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Address */}
          <Card>
            <CardHeader><CardTitle>Endereço</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rua</label>
                  <Input value={settings.address?.street || ''} onChange={e => handleNestedChange('address', 'street', e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cidade</label>
                  <Input value={settings.address?.city || ''} onChange={e => handleNestedChange('address', 'city', e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                  <Input value={settings.address?.state || ''} onChange={e => handleNestedChange('address', 'state', e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CEP</label>
                  <Input value={settings.address?.zipcode || ''} onChange={e => handleNestedChange('address', 'zipcode', e.target.value)} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Social */}
          <Card>
            <CardHeader><CardTitle>Redes Sociais</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Instagram</label>
                  <Input value={settings.social?.instagram || ''} onChange={e => handleNestedChange('social', 'instagram', e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Facebook</label>
                  <Input value={settings.social?.facebook || ''} onChange={e => handleNestedChange('social', 'facebook', e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn</label>
                  <Input value={settings.social?.linkedin || ''} onChange={e => handleNestedChange('social', 'linkedin', e.target.value)} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Business Hours */}
          <Card>
            <CardHeader><CardTitle>Horário de Funcionamento</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Segunda a Sexta</label>
                  <Input value={settings.businessHours?.monday_friday || ''} onChange={e => handleNestedChange('businessHours', 'monday_friday', e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sábado</label>
                  <Input value={settings.businessHours?.saturday || ''} onChange={e => handleNestedChange('businessHours', 'saturday', e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Domingo</label>
                  <Input value={settings.businessHours?.sunday || ''} onChange={e => handleNestedChange('businessHours', 'sunday', e.target.value)} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
